import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Key, Smartphone, Download, Trash2, AlertTriangle, FileText, Phone, Clipboard } from 'react-feather';
import { useAuth } from '../../contexts/AuthContext';

const AccountPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (!window.confirm('Är du säker på att du vill ta bort ditt konto? Detta kan inte ångras.')) {
      return;
    }

    setIsDeleting(true);
    try {
      // TODO: Implement delete account API call
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      
      // Log out user after account deletion
      await logout();
      alert('Ditt konto har tagits bort');
    } catch (error) {
      console.error('Delete account error:', error);
      alert('Kunde inte ta bort kontot. Försök igen senare.');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleExportData = async () => {
    try {
      // TODO: Implement data export API call
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      alert('Din data har exporterats och skickats till din email');
    } catch (error) {
      console.error('Export data error:', error);
      alert('Kunde inte exportera data. Försök igen senare.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="page-header bg-white shadow-sm border-b border-gray-200">
        <div className="page-header-content px-4 py-4">
          <div className="flex items-center">
            <button onClick={() => navigate('/settings')} className="mr-4">
              <span className="text-xl">‹</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Kontohantering</h1>
          </div>
        </div>
      </div>

      <div className="page-content px-4 py-6">
        <p className="text-gray-600 mb-6">Hantera din kontoinformation och säkerhetsinställningar</p>

        {/* Account Information */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Kontoinformation</h2>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Namn</span>
                <span className="font-medium text-gray-900">{user?.name || 'Inte angivet'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Email</span>
                <span className="font-medium text-gray-900">{user?.email || 'Inte angivet'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Inloggningsmetod</span>
                <span className="font-medium text-gray-900 capitalize">{user?.provider || 'Okänd'}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">Medlem sedan</span>
                <span className="font-medium text-gray-900">
                  {user?.createdAt ? formatDate(user.createdAt) : 'Okänt'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Säkerhet</h2>
          <div className="bg-white rounded-xl shadow-sm">
            <button className="w-full flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors">
              <div className="flex items-center">
                <Lock size={18} className="mr-3 text-gray-500" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Tvåfaktorsautentisering</p>
                  <p className="text-sm text-gray-600">Lägg till extra säkerhet till ditt konto</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </button>
            
            <button className="w-full flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors">
              <div className="flex items-center">
                <Key size={18} className="mr-3 text-gray-500" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Ändra lösenord</p>
                  <p className="text-sm text-gray-600">Uppdatera ditt lösenord för säkerhet</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </button>
            
            <button className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center">
                <Smartphone size={18} className="mr-3 text-gray-500" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Aktiva sessioner</p>
                  <p className="text-sm text-gray-600">Hantera inloggade enheter</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </button>
          </div>
        </div>

        {/* Data Management */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Datahantering</h2>
          <div className="bg-white rounded-xl shadow-sm">
            <button 
              onClick={handleExportData}
              className="w-full flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <Download size={18} className="mr-3 text-gray-500" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Exportera data</p>
                  <p className="text-sm text-gray-600">Ladda ner alla dina data som ZIP-fil</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </button>
            
            <button className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center">
                <Trash2 size={18} className="mr-3 text-gray-500" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Rensa cachad data</p>
                  <p className="text-sm text-gray-600">Ta bort lokal lagrad information</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </button>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-red-600 mb-4">Fara</h2>
          <div className="bg-white rounded-xl shadow-sm border border-red-200">
            <button 
              onClick={() => setShowDeleteModal(true)}
              className="w-full flex items-center justify-between p-4 hover:bg-red-50 transition-colors"
            >
              <div className="flex items-center">
                <AlertTriangle size={18} className="mr-3 text-gray-500" />
                <div className="text-left">
                  <p className="font-medium text-red-600">Ta bort konto</p>
                  <p className="text-sm text-red-500">Permanent borttagning av konto och alla data</p>
                </div>
              </div>
              <span className="text-red-400">›</span>
            </button>
          </div>
        </div>

        {/* Privacy & Legal */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Sekretess & Juridik</h2>
          <div className="bg-white rounded-xl shadow-sm">
            <button className="w-full flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors">
              <div className="flex items-center">
                <Clipboard size={18} className="mr-3 text-gray-500" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Integritetspolicy</p>
                  <p className="text-sm text-gray-600">Läs om hur vi hanterar dina data</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </button>
            
            <button className="w-full flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors">
              <div className="flex items-center">
                <FileText size={18} className="mr-3 text-gray-500" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Användarvillkor</p>
                  <p className="text-sm text-gray-600">Våra regler och villkor för användning</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </button>
            
            <button className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center">
                <Phone size={18} className="mr-3 text-gray-500" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Kontakta support</p>
                  <p className="text-sm text-gray-600">Få hjälp med ditt konto</p>
                </div>
              </div>
              <span className="text-gray-400">›</span>
            </button>
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="text-center mb-6">
              <AlertTriangle size={48} className="mx-auto mb-4 text-gray-300" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Ta bort konto</h2>
              <p className="text-gray-600">
                Är du säker på att du vill ta bort ditt konto? Detta kommer att permanent ta bort:
              </p>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <ul className="text-sm text-red-800 space-y-1">
                <li>• Alla dina transaktioner och budgetdata</li>
                <li>• Bankanslutningar och konton</li>
                <li>• Personliga inställningar</li>
                <li>• Konto och inloggningsinformation</li>
              </ul>
            </div>
            
            <div className="text-center mb-6">
              <p className="text-sm text-gray-600">
                <strong>Detta kan inte ångras.</strong> Överväg att exportera din data först.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                className="flex-1 bg-gray-600 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                onClick={() => setShowDeleteModal(false)}
              >
                Avbryt
              </button>
              <button
                className="flex-1 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
                onClick={handleDeleteAccount}
                disabled={isDeleting}
              >
                {isDeleting ? 'Tar bort...' : 'Ta bort konto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountPage;
